import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import _ from 'lodash';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { createConversation } from 'api/endpoints/chat';
import { getListChildrenForChat } from 'api/endpoints/children';
import Button from 'components/Button';
import { ENUM_EVENT_SOCKET } from 'constants/enum';
import { ChatContext } from 'pages/Chat/Chats';
import {
  setBabyId,
  setCurrentConversation,
  setDataCurrentConversation,
  setLoadingScreenChat,
  setMessagesHistory,
  setPaginationMessagesHistory,
} from 'store/actions/chat';
import { convertToQueryString, handleError } from 'utils/helper';

const Popup = ({ positionPopup, setShowPopupBabyList }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { sendMessage, getConversations } = useContext(ChatContext);

  const { me } = useSelector((state) => state.auth);

  const refList = useRef(null);
  const isCallingApi = useRef(false);

  const [babies, setBabies] = useState([]);
  const [queries, setQueries] = useState({ page: 1, page_size: 50, total: 0, keyword: '' });

  // CREATE CONVERSATION
  const handleCreateConversation = async (babyId) => {
    try {
      const queryString = convertToQueryString({ participant_id: babyId });
      const response = await createConversation(queryString);

      if (response?.data) {
        dispatch(setLoadingScreenChat(true));
        dispatch(setMessagesHistory([]));
        dispatch(setPaginationMessagesHistory({ page: 1, page_size: 50, total: 0 }));
        dispatch(setCurrentConversation(response.data.conversation_id));
        dispatch(setBabyId(babyId));
        dispatch(setDataCurrentConversation(response.data));
        await getConversations();
        sendMessage(ENUM_EVENT_SOCKET.JOIN_CONVERSATION, '', response.data.conversation_id, babyId);
        setShowPopupBabyList(false);

        setTimeout(() => {
          dispatch(setLoadingScreenChat(false));
        }, 500);
      }
    } catch (error) {
      handleError(error);
    }
  };

  // SEARCH CHILDREN
  const handleSearch = _.debounce((e) => {
    setQueries((prev) => ({ ...prev, page: 1, keyword: e.target.value }));
    setBabies([]);
  }, 500);

  // CHECK SCROLL IN END
  const checkScrollInEnd = () => {
    if (refList.current) {
      if (refList.current.scrollTop + refList.current.clientHeight >= refList.current.scrollHeight) {
        setQueries((prev) => ({
          ...prev,
          ...(prev.page + 1 <= Math.ceil(prev.total / prev.page_size) ? { page: prev.page + 1 } : {}),
        }));
      }
    }
  };

  // ADD EVENT LISTENER SCROLL
  useEffect(() => {
    if (refList.current) {
      refList.current.addEventListener('scroll', checkScrollInEnd);
    }
    return () => {
      if (refList.current) {
        refList.current.removeEventListener('scroll', checkScrollInEnd);
      }
    };
  }, [refList.current]);

  // CALL API GET CHILDREN
  const getChildren = useCallback(async () => {
    if (!isCallingApi.current) {
      try {
        isCallingApi.current = true;
        const queryString = convertToQueryString({
          page: queries.page,
          page_size: queries.page_size,
          keyword: queries.keyword,
        });
        const response = await getListChildrenForChat(queryString);
        if (response?.data && response?.data?.length > 0) {
          setBabies((prev) => [...prev, ...response.data]);
          setQueries((prev) => ({ ...prev, total: response.total }));
        }
      } catch (error) {
        handleError(error);
      } finally {
        isCallingApi.current = false;
      }
    }
  }, [me, queries.page, queries.keyword]);

  useEffect(() => {
    if (me) {
      getChildren();
    }
  }, [getChildren]);

  return ReactDOM.createPortal(
    <div className="fixed w-full h-full top-0 left-0 z-50">
      <div className="relative w-full h-full">
        <div id="backdrop" className="fixed inset-0 bg-black/10 z-1" onClick={() => setShowPopupBabyList(false)}></div>
        <div className="w-80 h-117 p-2 bg-white rounded shadow-md z-50 absolute" style={{ top: positionPopup.top + 3, left: positionPopup.left }}>
          <input
            onChange={handleSearch}
            type="text"
            placeholder={t('PLACEHOLDER_SEARCH_USER_LIST')}
            className="w-full h-10 p-2 rounded-sm border border-gray-300 outline-none"
          />
          <div className="p-1 h-[calc(100%-40px-8px)] overflow-auto mt-2" ref={refList}>
            <ul className="h-full space-y-2">
              {babies.length > 0 &&
                babies.map((baby, index) => (
                  <li
                    key={`baby-${index}`}
                    className="p-1 cursor-pointer hover:bg-gray-100 rounded-md"
                    onClick={() => handleCreateConversation(baby.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="w-10 min-w-10 h-10 min-h-10 rounded-full overflow-hidden bg-gray-300">
                        {baby.profile_picture && (
                          <img src={baby.profile_picture} alt={baby.name} width={40} height={40} className="size-full object-cover object-center" />
                        )}
                      </div>
                      <div className="w-full">
                        <p className="text-sm">
                          {baby.id} - {baby.name}
                        </p>
                        <p className="text-xs text-gray-500">{baby.user.email}</p>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const PopupBabyList = () => {
  const { t } = useTranslation();

  const refButton = useRef(null);

  const [showPopupBabyList, setShowPopupBabyList] = useState(false);
  const [positionPopup, setPositionPopup] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (refButton.current) {
      setPositionPopup({ top: refButton.current.offsetTop + refButton.current.offsetHeight, left: refButton.current.offsetLeft });
    }
  }, [refButton.current]);

  return (
    <>
      <div className="relative" ref={refButton}>
        <Button
          onClick={() => setShowPopupBabyList(!showPopupBabyList)}
          color="primary"
          text={t('NEW_CHAT_START')}
          className="w-51 h-9.5"
          icon={<img src={require('assets/resources/Plus.svg').default} alt="plus icon" width={18} height={18} />}
        />
      </div>
      {showPopupBabyList && <Popup positionPopup={positionPopup} setShowPopupBabyList={setShowPopupBabyList} />}
    </>
  );
};

export default PopupBabyList;
